import {
  createColorAliases,
  createColorComponentAliases,
  createDerivedColors,
} from "../colors";
import { createFonts } from "../fonts/fonts";
import { createRounding, createRoundingAliases } from "../rounding/rounding";
import { createShadows } from "../shadows/shadows";
import { createSpacing, createSpacingAliases } from "../spacing/spacing";
import { createText } from "../text/text";

import type { ThemeFonts } from "../fonts";
import type { ThemeRounding } from "../rounding";
import type { ThemeShadows } from "../shadows";
import type { ThemeSpacing } from "../spacing";
import type { ThemeText } from "../text";
import type {
  ThemeColor,
  ThemeColorAlias,
  ThemeColorComponentAlias,
  ThemeColors,
} from "../colors/types";
import type { ThemeConfig, createThemeFn } from "./types";

const themeConfigDefaults: ThemeConfig = {
  capitalizeButtonLabel: true,
  addDrawerDividers: true,
};

export const createTheme: createThemeFn = ({
  name,
  config,
  extend,
  customColorAliases,
  customDerivedColors,
  customColorComponentAliases,
  customShadows,
  customRoundingGlobals,
  customRoundingAliases,
  customSpacingAliases,
  customSpacingGlobals,
}) => {
  return (mode, os) => {
    const parentTheme = extend ? extend(mode, os) : undefined;

    // colors
    let derivedColors: Record<ThemeColor, string> = {
      ...createDerivedColors(mode),
    };

    // add in parent colors for derived colors
    if (parentTheme?.colors) {
      for (const derivedColor in derivedColors) {
        derivedColors[derivedColor as ThemeColor] =
          parentTheme.colors[derivedColor as ThemeColor];
      }
    }

    let colorAliases: Record<ThemeColorAlias, string> = {
      ...createColorAliases(derivedColors, mode),
    };

    // add in parent colors for color aliases
    if (parentTheme?.colors) {
      for (const colorAlias in colorAliases) {
        colorAliases[colorAlias as ThemeColorAlias] =
          parentTheme.colors[colorAlias as ThemeColorAlias];
      }
    }

    let colorSemanticAliases: Record<ThemeColorComponentAlias, string> = {
      ...createColorComponentAliases(derivedColors, colorAliases, mode),
    };

    // add in parent colors for color semantic aliases
    if (parentTheme?.colors) {
      for (const colorSemanticAlias in colorSemanticAliases) {
        colorSemanticAliases[colorSemanticAlias as ThemeColorComponentAlias] =
          parentTheme.colors[colorSemanticAlias as ThemeColorComponentAlias];
      }
    }

    if (customDerivedColors) {
      derivedColors = {
        ...derivedColors,
        ...customDerivedColors(mode),
      };
      colorAliases = {
        ...colorAliases,
        ...createColorAliases(derivedColors, mode),
      };
      colorSemanticAliases = {
        ...createColorComponentAliases(derivedColors, colorAliases, mode),
      };
    }

    if (customColorAliases) {
      colorAliases = {
        ...colorAliases,
        ...customColorAliases(derivedColors, mode),
      };
      colorSemanticAliases = {
        ...createColorComponentAliases(derivedColors, colorAliases, mode),
      };
    }

    if (customColorComponentAliases) {
      colorSemanticAliases = {
        ...colorSemanticAliases,
        ...customColorComponentAliases(derivedColors, colorAliases, mode),
      };
    }

    const colors: ThemeColors = {
      ...derivedColors,
      ...colorAliases,
      ...colorSemanticAliases,
    };

    // fonts
    const fonts: ThemeFonts = {
      ...createFonts(os),
      ...(parentTheme ? parentTheme.fonts : {}),
    };

    // text
    const text: ThemeText = {
      ...createText(os),
      ...(parentTheme ? parentTheme.text : {}),
    };

    // shadows
    const shadows: ThemeShadows = {
      ...createShadows(colors, mode),
      ...(parentTheme ? parentTheme.shadows : {}),
      ...(customShadows ? customShadows(colors, mode) : undefined),
    };

    // rounding
    let rounding: ThemeRounding = {
      ...createRounding(),
      ...(parentTheme ? parentTheme.rounding : {}),
    };

    if (customRoundingGlobals) {
      rounding = {
        ...rounding,
        ...customRoundingGlobals(),
        ...createRoundingAliases({
          ...rounding,
          ...customRoundingGlobals(),
        }),
      };
    }

    if (customRoundingAliases) {
      rounding = {
        ...rounding,
        ...customRoundingAliases(rounding),
      };
    }

    // spacing
    let spacing: ThemeSpacing = {
      ...createSpacing(),
      ...(parentTheme ? parentTheme.spacing : {}),
    };

    if (customSpacingGlobals) {
      spacing = {
        ...spacing,
        ...customSpacingGlobals(),
        ...createSpacingAliases({
          ...spacing,
          ...customSpacingGlobals(),
        }),
      };
    }

    if (customSpacingAliases) {
      spacing = {
        ...spacing,
        ...customSpacingAliases(spacing),
      };
    }

    return {
      name,
      colors,
      fonts,
      shadows,
      rounding,
      spacing,
      text,
      config: {
        ...themeConfigDefaults,
        ...config,
      },
    };
  };
};
